import { useMutation } from '@apollo/client';
import { ButtonDock } from '@focuslee/shared-mini-app';
import { FC, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CompleteGoalsMutation } from '../client/generated/graphql';
import { completeGoalsMutation } from '../client/mutations/completeGoals';
import { ListCategory } from '../client/types';
import { GoalsListView } from '../components/common/GoalsListView';
import { getTotalReward } from '../helpers/goal';
import { useCachedCategoriesWithFallback } from '../hooks/caching/category';
import { useCachedGoalsWithFallback } from '../hooks/caching/goal';
import { useCachedUserGoalCompletion } from '../hooks/caching/user';
import { useFeatureFlag } from '../contexts/FeatureFlagContext';
import { goalsConfigurePath } from '../navigation/paths';

export const GoalsCompletePage: FC = () => {
  const navigate = useNavigate();
  const [completeGoals, { loading: completing }] = useMutation<CompleteGoalsMutation>(completeGoalsMutation);

  const { goals, loading: goalsLoading } = useCachedGoalsWithFallback();
  const { categories, loading: categoriesLoading } = useCachedCategoriesWithFallback();
  const goalCompletion = useCachedUserGoalCompletion();

  const loading = goalsLoading || categoriesLoading;

  const handleClaim = useCallback(async () => {
    try {
      await completeGoals({ variables: { skipCompletion: false } });
      navigate(`${goalsConfigurePath}?byPass=true`);
    } catch (error) {
      console.error(error);
    }
  }, [completeGoals, navigate]);

  const categoryMap: Record<string, ListCategory> = useMemo(() =>
    Object.fromEntries(categories.map(c => [c.id, c])),
    [categories]
  );

  const activeGoals = useMemo(() => goals.filter(g => g.active), [goals]);
  const totalReward = useMemo(() => getTotalReward(activeGoals), [activeGoals]);
  const rewardsEnabled = useFeatureFlag('rewards');

  return (
    <GoalsListView
      activeGoals={activeGoals}
      goalCompletion={goalCompletion}
      categoryMap={categoryMap}
      loading={loading}
      footer={
        <ButtonDock
          main={{
            text: rewardsEnabled && totalReward > 0 ? `Claim ${totalReward} coins` : 'Continue',
            onClick: handleClaim,
            loading: loading || completing,
          }}
        />
      }
    />
  );
};